import {
	ConvoAIWebSocketAPI,
	type ConvoAIWebSocketServerMessage,
	ConvoAIWebSocketServerMessageType,
} from '../api/convo-ai/ConvoAIWebSocketAPI';
import { processAudioToTextWithPCM16 } from './processAudioToText';
import {
	VoiceDictationStopReason,
	type WebSpeechToTextAPIAction,
	type WebSpeechToTextTraceAction,
} from './WebSpeechToTextModels';

const getSupportedMimeType = (): string | undefined => {
	// Order of preference: webm (best for streaming), then mp4/aac (Safari)
	const mimeTypes = [
		'audio/webm;codecs=opus',
		'audio/webm',
		'audio/mp4',
		'audio/aac',
		'audio/ogg;codecs=opus',
	];

	// Access MediaRecorder via window to avoid lint errors for unsupported browsers
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const MediaRecorderAPI = (window as any).MediaRecorder as typeof MediaRecorder | undefined;

	// Check if MediaRecorder.isTypeSupported exists (not available in older browsers)
	if (!MediaRecorderAPI || typeof MediaRecorderAPI.isTypeSupported !== 'function') {
		return undefined;
	}

	return mimeTypes.find((mimeType) => MediaRecorderAPI.isTypeSupported(mimeType)) ?? undefined;
};

export type WebSpeechToTextManagerOptions = {
	audioBitsPerSecond?: number;
	cloudID: string;
	eagerness?: string;
	enableGhostText?: boolean;
	experience?: string;
	maxRecordingDurationMs?: number;
	/**
	 * Transcription model to request in `audio.session.update`. When omitted,
	 * the server uses its default. Only takes effect when `skipSessionUpdate`
	 * is false.
	 */
	model?: string;
	onTrace?: (action: WebSpeechToTextTraceAction) => void;
	onTranscription: (action: WebSpeechToTextAPIAction) => void;
	product?: string;
	sampleRate?: number;
	/**
	 * When `true`, no `audio.session.update` message will be sent after the
	 * WebSocket connects. The server uses its default session config (driven
	 * by a server-side feature flag). When `true`, `vadType` and `eagerness`
	 * are no-ops.
	 *
	 * @default false
	 */
	skipSessionUpdate?: boolean;
	timeSlice?: number;
	vadType?: string;
};

export class WebSpeechToTextManager {
	private audioChunks: Blob[] = [];
	private lastProcessedPosition = 0;
	private processedChunksCountAfterLastCommit = 0;
	private convoAIWebSocketAPI: ConvoAIWebSocketAPI | null = null;
	private mediaStream: MediaStream | null = null;
	private mediaRecorder: MediaRecorder | null = null;
	private lastAudioChunkWasProcessed = false;
	private recordingTimeout: NodeJS.Timeout | null = null;
	private transcription: string | null = null;
	private chunkIndex = 0;
	private createdItemChunkIndex = 0;
	private sessionReadyDispatched = false;
	private finalResponseListener: (() => void) | null = null;
	private isStopped: boolean = false;

	private readonly cloudID: string;
	private readonly sampleRate: number;
	private readonly audioBitsPerSecond: number;
	private readonly timeSlice: number;
	private readonly maxRecordingDurationMs: number;
	private readonly product: string;
	private readonly experience: string;
	private readonly vadType?: string;
	private readonly eagerness?: string;
	private readonly model?: string;
	private readonly skipSessionUpdate: boolean;
	private readonly onTrace?: (action: WebSpeechToTextTraceAction) => void;
	private readonly onTranscription: (action: WebSpeechToTextAPIAction) => void;

	constructor(options: WebSpeechToTextManagerOptions) {
		const {
			onTranscription,
			cloudID,
			sampleRate = 24000,
			audioBitsPerSecond = 16000,
			timeSlice = 1000,
			maxRecordingDurationMs = 60000,
			onTrace,
			product = 'confluence',
			experience = 'confluence-ai-first-creation',
			vadType,
			eagerness,
			model,
			skipSessionUpdate = false,
		} = options;

		this.onTranscription = onTranscription;
		this.cloudID = cloudID;
		this.sampleRate = sampleRate;
		this.audioBitsPerSecond = audioBitsPerSecond;
		this.timeSlice = timeSlice;
		this.maxRecordingDurationMs = maxRecordingDurationMs;
		this.onTrace = onTrace;
		this.product = product;
		this.experience = experience;
		this.vadType = vadType;
		this.eagerness = eagerness;
		this.model = model;
		this.skipSessionUpdate = skipSessionUpdate;
	}

	public async startRecording(): Promise<void> {
		this.resetInMemoryState();

		this.onTranscription({ type: 'SET_INITIALIZING' });
		await this.connectWebSocketConvoAIAPI();

		this.mediaStream = await navigator.mediaDevices.getUserMedia({
			audio: {
				sampleRate: this.sampleRate,
				echoCancellation: true,
				noiseSuppression: true,
				autoGainControl: true,
			},
		});

		const mimeType = getSupportedMimeType();

		const mediaRecorderOptions: MediaRecorderOptions = {
			audioBitsPerSecond: this.audioBitsPerSecond,
		};
		if (mimeType) {
			mediaRecorderOptions.mimeType = mimeType;
		}
		// eslint-disable-next-line compat/compat
		this.mediaRecorder = new MediaRecorder(this.mediaStream, mediaRecorderOptions);

		this.mediaRecorder.start(this.timeSlice);

		this.mediaRecorder.addEventListener('dataavailable', this.onMediaRecorderDataAvailable);
		this.mediaRecorder.addEventListener('stop', this.onMediaRecorderStop);
		this.mediaRecorder.addEventListener('error', this.onMediaRecorderError);

		this.recordingTimeout = setTimeout(() => {
			this.stopRecording(VoiceDictationStopReason.TimeoutAccepted);
		}, this.maxRecordingDurationMs);
	}

	public stopRecording(
		stopReason: VoiceDictationStopReason = VoiceDictationStopReason.StoppedAccepted,
	): void {
		this.insertEmptySpaceIntoTranscription();
		this.clearRecordingTimeout();
		this.onTrace?.({ type: 'STOP_RECORDING', payload: { stopReason } });
		this.isStopped = true;
		this.onTranscription({ type: 'SET_STOPPED', payload: { stopReason } });
		this.mediaRecorder?.stop();
	}

	private async connectWebSocketConvoAIAPI() {
		try {
			this.onTrace?.({ type: 'START_WEBSOCKET_CONNECTION' });
			this.convoAIWebSocketAPI = await ConvoAIWebSocketAPI.create(
				this.cloudID,
				this.product,
				this.experience,
				this.onMessage,
			);
			this.onTrace?.({ type: 'WEBSOCKET_CONNECTION_SUCCESS' });
			if (!this.skipSessionUpdate) {
				this.convoAIWebSocketAPI.updateTranscriptionConfig(
					undefined,
					undefined,
					this.vadType,
					this.eagerness,
					this.model,
				);
			}
		} catch (error) {
			this.onTranscription({
				type: 'SET_ERROR',
				payload: { error: error instanceof Error ? error.message : 'Unknown error' },
			});
			this.onTrace?.({
				type: 'ERROR',
				payload: { error: error instanceof Error ? error.message : 'Unknown error' },
			});
		}
	}

	private insertEmptySpaceIntoTranscription() {
		if (this.transcription) {
			this.transcription = this.transcription + ' ';
			this.onTranscription({
				type: 'SET_RECORDING',
				payload: { transcription: this.transcription, chunkIndex: this.chunkIndex },
			});
		}
	}

	private onMessage = (event: ConvoAIWebSocketServerMessage) => {
		switch (event.type) {
			case ConvoAIWebSocketServerMessageType.TranscriptionSessionCreated:
				// Server has set up the transcription session and is ready to receive
				// audio. Flip out of `Initializing` immediately so the loading state
				// clears in ~hundreds of ms (vs waiting for the first delta which may
				// not arrive for several seconds depending on server VAD silence
				// thresholds and whether the user has started speaking yet).
				if (!this.sessionReadyDispatched) {
					this.sessionReadyDispatched = true;
					this.onTranscription({
						type: 'SET_RECORDING',
						payload: { transcription: '', chunkIndex: 0 },
					});
				}
				break;
			case ConvoAIWebSocketServerMessageType.TranscriptionResponseDelta:
				this.transcription =
					this.transcription !== null ? this.transcription + event.delta : event.delta;
				this.chunkIndex++;
				this.onTranscription({
					type: 'SET_RECORDING',
					payload: { transcription: this.transcription, chunkIndex: this.chunkIndex },
				});
				break;
			case ConvoAIWebSocketServerMessageType.TranscriptionResponseCompleted:
				// Server has finalized an utterance. Insert a separator (single
				// space) before the next utterance starts so that consecutive
				// utterances don't visually fuse together in the rendered
				// transcript (e.g. "EnglishOh." → "English Oh."). This is the
				// only signal the gateway emits between utterances for
				// gpt-realtime-whisper, where TranscriptionItemCreated does not
				// fire.
				// Only insert space if the session has not been stopped.
				// If isStopped is true, stopRecording() has already been called and
				// dispatching SET_RECORDING here would flip the UI back to listening
				// after the user has explicitly stopped — causing the auto-resume bug.
				if (!this.isStopped) {
					this.insertEmptySpaceIntoTranscription();
				}
				// If we are in the middle of a graceful shutdown (`stopRecording`
				// flushed remaining audio and is waiting for the server to
				// finalize before closing), this signals "the server is done."
				if (this.finalResponseListener) {
					const listener = this.finalResponseListener;
					this.finalResponseListener = null;
					listener();
				}
				break;
			case ConvoAIWebSocketServerMessageType.TranscriptionItemCreated:
				this.createdItemChunkIndex++;
				this.onTrace?.({
					type: 'TRANSCRIPTION_ITEM_CREATED',
					payload: { createdItemChunkIndex: this.createdItemChunkIndex },
				});
				this.insertEmptySpaceIntoTranscription();
				break;
		}
	};

	private onMediaRecorderStop = () => {
		this.mediaStream?.getTracks().forEach((track) => track.stop());

		// Flush any in-flight audio bytes the data-available pipeline hadn't
		// yet committed, then give the server a brief window to finalize the
		// current utterance (emit response.completed) before tearing down the
		// WebSocket. Without this, the trailing N hundred milliseconds of
		// audio reach the server but get dropped because we close before the
		// final transcription.response.delta / response.completed are emitted.
		const api = this.convoAIWebSocketAPI;
		if (!api) {
			return;
		}
		try {
			api.commitAudio();
		} catch {
			// commitAudio may throw if the socket has already closed; in that
			// case there is nothing more to wait for.
			api.disconnect();
			return;
		}

		const FINAL_RESPONSE_WAIT_MS = 2000;
		let settled = false;
		const finish = () => {
			if (settled) return;
			settled = true;
			this.finalResponseListener = null;
			api.disconnect();
		};

		this.finalResponseListener = finish;
		setTimeout(finish, FINAL_RESPONSE_WAIT_MS);
	};

	private onMediaRecorderError = (event: MediaRecorderEventMap['error']) => {
		this.onTranscription({ type: 'SET_ERROR', payload: { error: event.message } });
		this.onTrace?.({ type: 'ERROR', payload: { error: event.message } });
	};

	private onMediaRecorderDataAvailable = async (event: MediaRecorderEventMap['dataavailable']) => {
		this.audioChunks.push(event.data);

		try {
			const { audioString, newAudioEndPosition } = await processAudioToTextWithPCM16(
				this.audioChunks,
				this.lastProcessedPosition,
				this.sampleRate,
			);
			this.lastProcessedPosition = newAudioEndPosition;

			if (audioString) {
				this.lastAudioChunkWasProcessed = true;
				this.processedChunksCountAfterLastCommit++;
				this.convoAIWebSocketAPI?.sendAudio(audioString);
			} else if (audioString === null && this.lastAudioChunkWasProcessed) {
				this.convoAIWebSocketAPI?.commitAudio();
				this.processedChunksCountAfterLastCommit = 0;
				this.lastAudioChunkWasProcessed = false;
			}
		} catch (error) {
			this.onTranscription({
				type: 'SET_ERROR',
				payload: { error: error instanceof Error ? error.message : 'Unknown error' },
			});
			this.onTrace?.({
				type: 'ERROR',
				payload: { error: error instanceof Error ? error.message : 'Unknown error' },
			});
		}
	};

	private clearRecordingTimeout() {
		if (this.recordingTimeout) {
			clearTimeout(this.recordingTimeout);
			this.recordingTimeout = null;
		}
	}

	private resetInMemoryState() {
		this.isStopped = false;
		this.chunkIndex = 0;
		this.createdItemChunkIndex = 0;
		this.sessionReadyDispatched = false;
		this.finalResponseListener = null;
		this.lastProcessedPosition = 0;
		this.processedChunksCountAfterLastCommit = 0;
		this.lastAudioChunkWasProcessed = false;
		this.transcription = null;
		this.audioChunks = [];
		this.clearRecordingTimeout();
	}
}
